/**
 * Маппинг textAlign (формат "horizontal-vertical", как в diagram-js и бэкенде)
 * в атрибуты подписи X6: textAnchor, textVerticalAnchor, refX, refY, refX2, refY2.
 *
 * Центр (center / middle) — доли bbox (0.5), чтобы текст оставался по центру при ресайзе.
 * Края (left / right / top / bottom) — фиксированные пиксели от границы фигуры,
 * иначе X6 масштабирует процентный якорь и подпись «уплывает».
 */
const H = {left: 'start', center: 'middle', right: 'end'};
const V = {top: 'top', middle: 'middle', bottom: 'bottom'};

/** Отступ подписи от края фигуры, как у Note / Group. */
export const LABEL_EDGE_PADDING = 8;

/**
 * Правый отступ, когда в углу есть picto-иконка (якорь иконки: 100% − 30px).
 * Фиксированные пиксели, чтобы при ресайзе текст не наезжал на лого.
 */
export const LABEL_ICON_RIGHT_PADDING = 36;

const REF_Y = {top: LABEL_EDGE_PADDING, middle: 0.5, bottom: '100%'};
const REF_Y2 = {top: 0, middle: 0, bottom: -LABEL_EDGE_PADDING};

const DEFAULT = {
	textAnchor: 'middle',
	textVerticalAnchor: 'top',
	refX: 0.5,
	refX2: 0,
	refY: LABEL_EDGE_PADDING,
	refY2: 0,
};

/**
 * @param {string} [textAlign] — например 'center-top', 'left-middle'
 * @param {{ defaultHorizontal?: string, defaultVertical?: string, reserveForIcon?: boolean }} [defaults]
 *        дефолт при отсутствии textAlign (для Note: left-top);
 *        reserveForIcon — увеличить правый отступ под picto-иконку
 * @returns {{ textAnchor: string, textVerticalAnchor: string, refX: number|string, refX2: number, refY: number|string, refY2: number }}
 */
export function textAlignToLabelAttrs(textAlign, defaults = {}) {
	const defH = defaults.defaultHorizontal ?? 'center';
	const defV = defaults.defaultVertical ?? 'top';
	const rightPadding = defaults.reserveForIcon ? LABEL_ICON_RIGHT_PADDING : LABEL_EDGE_PADDING;
	const REF_X = {left: LABEL_EDGE_PADDING, center: 0.5, right: '100%'};
	const REF_X2 = {left: 0, center: 0, right: -rightPadding};

	const fallback = {
		textAnchor: H[defH] ?? DEFAULT.textAnchor,
		textVerticalAnchor: V[defV] ?? DEFAULT.textVerticalAnchor,
		refX: REF_X[defH] ?? DEFAULT.refX,
		refX2: REF_X2[defH] ?? DEFAULT.refX2,
		refY: REF_Y[defV] ?? DEFAULT.refY,
		refY2: REF_Y2[defV] ?? DEFAULT.refY2,
	};

	if (!textAlign || typeof textAlign !== 'string') {
		return fallback;
	}

	const parts = textAlign.split('-');
	const h = (parts[0] || defH).toLowerCase();
	const v = (parts[1] || defV).toLowerCase();

	return {
		textAnchor: H[h] ?? fallback.textAnchor,
		textVerticalAnchor: V[v] ?? fallback.textVerticalAnchor,
		refX: REF_X[h] ?? fallback.refX,
		refX2: REF_X2[h] ?? fallback.refX2,
		refY: REF_Y[v] ?? fallback.refY,
		refY2: REF_Y2[v] ?? fallback.refY2,
	};
}
